/**
 * McpQuickEnableChip
 *
 * One-click "MCP: off · Enable read-only" chip in the AI terminal context
 * bar's right-hand chip group (Task T8, AI Smoothness Wave). Lets a user
 * turn on the local MCP server without leaving the terminal or first
 * understanding what MCP is.
 *
 * Per wave decision D-2: the click enables the server with
 * `permissionLevel: 'read-only'` SILENTLY (no confirmation dialog) — the
 * read-only default is what makes that silence safe — then shows a
 * confirmation toast linking back to Settings → AI Integrations for anyone
 * who wants to widen permissions.
 *
 * Uses `mcpServer.getStatusLight()` for the initial read, never
 * `mcpServer.getConfig()` — the latter touches the OS keychain and can
 * prompt the user. The on/off state (both the initial render and the result
 * of a click) is keyed off `status.running`, not `config.enabled`:
 * `updateConfig` persists `enabled: true` before it attempts to start the
 * server, so a start failure (e.g. a port conflict) would otherwise leave
 * the chip lying about being on.
 *
 * Status is polled (not fetched once) on the same 5s cadence as the sibling
 * `useMcpRunning` hook in `AiChatSidebar.tsx` — MCP can be enabled or
 * reconfigured elsewhere (Settings → AI Integrations) while this chip stays
 * mounted, and a stale "off" reading is not just cosmetic here: clicking a
 * stale chip would re-run `updateConfig({ permissionLevel: 'read-only' })`
 * and silently downgrade a stricter permission level the user already
 * chose. Polling narrows that window but cannot close it, so `handleEnable`
 * re-reads status immediately before writing and skips the write entirely
 * if MCP is already running — see the comment there.
 */

import { useEffect, useRef, useState } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { toast } from '../../../components/ui/toaster';
import { useSettingsDialogStore } from '../../main-toolbar/store/settingsDialogStore';
import type { McpServerStatus } from '../../../types/mcpServerTypes';

export function McpQuickEnableChip() {
  const [status, setStatus] = useState<McpServerStatus | null>(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const { track } = useAnalytics();
  // Guards against a double-click sending the enable request twice (and
  // double-firing analytics/toast) — same pattern as AgentStartButtons.
  const enableInFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const refreshStatus = () => {
      window.electronAPI?.mcpServer
        ?.getStatusLight?.()
        .then((result) => {
          if (cancelled) return;
          if (result.success && result.status) {
            setStatus(result.status);
          }
        })
        .catch(() => {
          // Leave status unset — the chip stays hidden rather than guessing.
        });
    };

    refreshStatus();
    const interval = setInterval(refreshStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Not yet loaded, or electronAPI unavailable (browser mode) — stay hidden
  // rather than render a state we haven't confirmed.
  if (!status) {
    return null;
  }

  if (status.running) {
    return (
      <span
        data-testid="mcp-status-chip"
        className="text-[10px] font-medium text-muted-foreground"
      >
        MCP: on
      </span>
    );
  }

  const handleEnable = async (e: React.MouseEvent) => {
    // The collapsed context bar is itself a <button> that toggles the
    // expanded details panel — stop the click from bubbling into it, same
    // pattern as the nested PRO badge and the agent Start buttons.
    e.stopPropagation();

    if (enableInFlightRef.current) {
      return;
    }
    enableInFlightRef.current = true;
    setIsEnabling(true);

    try {
      // Polling narrows the stale-status window but cannot close it — MCP
      // may have been enabled from Settings (possibly with a stricter
      // `permissionLevel` than read-only) in the moments since the last
      // poll tick. Re-read the live status right before writing: if the
      // server is already running, do NOT call `updateConfig` at all — this
      // chip must never be capable of lowering an already-chosen permission
      // level. Just refresh local state (which hides the chip) and stop.
      // No config was written, so there is no side effect to report: no
      // toast, no `feature:mcp_quick_enable` analytics event.
      const current = await window.electronAPI?.mcpServer?.getStatusLight?.();
      if (current?.success && current.status?.running) {
        setStatus(current.status);
        return;
      }

      const result = await window.electronAPI?.mcpServer?.updateConfig({
        enabled: true,
        permissionLevel: 'read-only',
      });

      // Only report this as a real activation once the server is confirmed
      // running — see the module doc comment on why `status.running` (not
      // `result.success` / `config.enabled`) is the source of truth.
      if (result?.success && result.status?.running) {
        setStatus(result.status);
        track('feature:mcp_quick_enable', { source: 'terminal_bar' });
        toast.success({
          title: 'MCP enabled (read-only)',
          description:
            'Agents can now run SELECT queries — change permissions in Settings → AI Integrations.',
          action: {
            label: 'Settings',
            onClick: () => useSettingsDialogStore.getState().openToSection('ai-integrations'),
          },
        });
      } else {
        toast.error({
          title: 'Could not enable MCP',
          description: result?.error || 'The MCP server failed to start.',
        });
      }
    } catch (err) {
      toast.error({
        title: 'Could not enable MCP',
        description: err instanceof Error ? err.message : 'The MCP server failed to start.',
      });
    } finally {
      enableInFlightRef.current = false;
      setIsEnabling(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleEnable}
      disabled={isEnabling}
      data-testid="mcp-quick-enable"
      title="Enable MCP with read-only access for AI agents"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium hover:bg-primary/20 transition-colors disabled:opacity-60"
    >
      {isEnabling && <RandomDotmSpinner size={10} />}
      MCP: off · Enable read-only
    </button>
  );
}
